/// <reference types="activex" />
import Object from 'apmui/util/Object';
import { divLog } from 'apmui/util/divLog';
import setImmediate from 'apmui/util/setImmediate';
import { IComponentPackagesWindows } from './Model';

/**
 * Ids of components as "serverName.typeName", where serverName is the name
 * of the application providing the object, typeName is the name of the type or class
 * of the object to create
 */
enum COMPONENT_ID {
    INSTALLER_CONTROL_MODERN = 'InstallerControl.InstControl2',
    INSTALLER_CONTROL_LEGACY = 'InstallerControl.InstControl',
    INSPECTION_HOST = 'InspectionHost.UAgentHost',
}

/**
 * Check whether ActiveX with given name is installed and enabled in browser
 * @param id tested component id
 * @return true if active x with given name exists, false if doesn't
 */
function checkActiveX(id: COMPONENT_ID) {
    divLog('checkActiveXControl - enter name=' + id);

    let ret;
    try {
        const control = { activeX: new ActiveXObject(id) };
        delete control.activeX;
        ret = true;
    } catch (e) {
        ret = false;
    }

    divLog('checkActiveXControl - exit', String(ret));

    return ret;
}

/** DOM container for <object> */
let container: HTMLElement;

/**
 * function creates <OBJECT> in container
 * @param id <object id=...>
 * @param attributes map of attributes to put into object other than id
 * @param params map of param tags to put into object in [name]:value format
 * @param event map of event reffering code in [event]:handler list
 */
function createObject(
    id: string,
    attributes: Record<string, string>,
    params: Record<string, string | number>,
    events: Record<string, string>
) {
    const attrStr = Object.entries(attributes)
        .map((x) => `${x[0]}="${x[1]}"`)
        .join(' ');

    const parStr = Object.entries(params)
        .map((x) => `<PARAM NAME="${x[0]}" VALUE="${x[1]}">`)
        .join('');

    container.innerHTML = '';

    for (const [event, handler] of Object.entries(events)) {
        const script = document.createElement('script');
        script.setAttribute('for', id);
        script.event = event;
        script.appendChild(document.createTextNode(handler));
        container.appendChild(script);
    }

    container.innerHTML += `<object ID="${id}" ${attrStr} width=1 height=1>${parStr}</object>`;
}

/**
 * EVETNS list that might be send to callback divided in 3 groups:
 * 1. Strictly UI events
 * 2. Function behavior events
 * 3. Object/plugin callback evetns. Third groups are alson names of CB functions
 */
enum EVENT {
    // Direct actions
    INIT = 'init',
    CHANGE_TEXT = 'changeText',
    CHANGE_PROGRESS = 'changeProgress',
    CHANGE_URL = 'changeUrl',
    NO_INSPECTION_HOST = 'noInspectionHost',

    // Calling functions
    INSTALLATION_DONE = 'installationDone',
    INSTALLER_CONTROL_CHECK = 'installerControlCheck',
    INSTALLER_CONTROL_RECHECK = 'installerControlRecheck',
    INSPECTION_HOST_INSTALL = 'inspectionHostInstall',

    // Object/Plugin callbacks
    LEGACY_INSTALLER_CONTROL_UPGRADE = 'legacyInstallerControlUpgrade',
    INSTALLER_CONTROL_UPDATE = 'installerControlUpgrade',
    INSPECTION_HOST_ERROR = 'inspectionHostError',
    INSPECTION_HOST_EVENT = 'inspectionHostCall',
}

/** generated id for <object> */
const objectId: string = 'UINST' + String(Math.random());
/** packages data */
let packages: IComponentPackagesWindows;

let session: string;
let baseUrl = '';
type ICallback = (event: EVENT, status: string, arg: string) => void;
let callback: ICallback;

/**
 * This functions installs and runs activeX component. Approx call sequence:
 * Checks if Legacy Installer Control is installed and trying to upgrade it
 *      LegacyInstallerControlUpgrade
 * Check and if installed try to upgrade Modern Installer Control
 *      InstallerControlCheck, installerControlRecheck, installerControlUpdate
 * Check and Run Inspection Host
 *      inspectionHostRun
 * @param componentPackages supplied in page.json
 * @param sess mrh session id
 * @param url base url of server
 * @param cb callback function
 */
function installActiveX(componentPackages: IComponentPackagesWindows, sess: string, url: string, cb: ICallback) {
    divLog('installActiveX - enter');

    packages = componentPackages;
    session = sess;
    baseUrl = url;
    callback = cb ? cb : callback;

    if (!container) {
        container = document.body.appendChild(document.createElement('div'));
        // object doesn't work this way
        // container.hidden = true;
        container.id = 'apmui-plugin-container';
    }

    callback(EVENT.INIT, '', '');
    if (checkActiveX(COMPONENT_ID.INSTALLER_CONTROL_LEGACY)) {
        // InstallerControl is installed - continue with target control verification
        setImmediate(() => LegacyInstallerControlUpgrade());
    } else {
        InstallerControlCheck();
    }

    divLog('installActiveX - exit');
}

/**
 * Update and upgrade events statuses including not sent by host
 */
enum STATUS {
    // IMAGINARY
    CREATE_FAILED = '-1',
    STARTED = '0',
    // REAL
    SEQUENCE_FAILED = '3',
    SEQUENCE_COMPLETED = '4',
    DONE = '5',
}

/**
 * Checks if Legacy Installer Control is installed and trying to upgrade it
 * Callback function is used
 */
function LegacyInstallerControlUpgrade() {
    divLog('LegacyInstallerControlUpgrade - enter');

    callback(EVENT.LEGACY_INSTALLER_CONTROL_UPGRADE, String(STATUS.STARTED), '');
    try {
        const attributes = {
            CLASSID: packages.installerControl2015.id,
        };
        const params = {
            AutoLoad: '0',
            InstallerServiceUpdate:
                `/URL:${baseUrl}${packages.installerService2015.url}#${packages.installerService2015.version}` +
                ` /ID: ${packages.installerService2015.id}` +
                `\n/URL:${baseUrl}${packages.installerService.url}#${packages.installerService.version}` +
                ` /ID: ${packages.installerService.id}`,
            InstallSequence:
                `/URL:${baseUrl}${packages.installerControl2015.url}#${packages.installerControl2015.version}` +
                ` /ID:${packages.installerControl2015.id}` +
                ` /TASKID: ${baseUrl}${packages.installerControl2015.url}#${packages.installerControl2015.version}` +
                `\n`,
        };
        const events = {
            'InstallProgress(a_taskID, a_state, a_progress)':
                EVENT.LEGACY_INSTALLER_CONTROL_UPGRADE + '(a_taskID, a_state, a_progress);',
        };

        // create find and run object
        createObject(objectId, attributes, params, events);
        const object = document.getElementById(objectId);
        (object as any).run();
    } catch (e) {
        divLog('LegacyInstallerControlUpgrade Exception: ' + e.description);
        callback(EVENT.LEGACY_INSTALLER_CONTROL_UPGRADE, String(STATUS.CREATE_FAILED), ''); // TODO fix me
    }

    divLog('LegacyInstallerControlUpgrade - exit');
}

/**
 * LegacyInstallerControlUpgrade's object callback
 * @param taskId execution tracking number
 * @param status installation status
 * @param prog progress string
 */
window[EVENT.LEGACY_INSTALLER_CONTROL_UPGRADE] = (taskId: number, status: STATUS, prog: number) => {
    divLog(`F:${EVENT.LEGACY_INSTALLER_CONTROL_UPGRADE} - enter tid=${taskId}, ${status}, ${prog}`);

    callback(EVENT.LEGACY_INSTALLER_CONTROL_UPGRADE, String(status), String(prog));
    if (String(status) === String(STATUS.DONE)) {
        setImmediate(() => InstallerControlCheck());
    }

    divLog(`${EVENT.LEGACY_INSTALLER_CONTROL_UPGRADE} - exit`);
};

/**
 * Checks if (Modern) Installer Control is installed and trying to update it
 * Callback function is used
 * If it's not installed it's trying to create temp object, load it and recheck
 */
function InstallerControlCheck() {
    divLog('InstallerControlCheck - enter');

    if (checkActiveX(COMPONENT_ID.INSTALLER_CONTROL_MODERN)) {
        divLog('InstallerControlCheck - controll installed');
        // InstallerControl is installed - continue with target control verification / update
        setImmediate(() => installerControlUpdate());
    } else {
        // InstallerControl is not installed - try to install temp and recheck it
        divLog('InstallerControlCheck - no controll installed');
        const tmpId = '_tmp_install_object';
        const attributes = {
            CLASSID: packages.installerControl.id,
            CODEBASE: packages.installerControl.url,
        };
        createObject(tmpId, attributes, {}, {});
        callback(EVENT.INSTALLER_CONTROL_RECHECK, String(STATUS.STARTED), '');
        installerControlRecheck();
    }

    divLog('InstallerControlCheck - exit');
}

let timerId: number;
/** number of rechecks to perform */
let countdown = 15;
/**
 * Waiting for (Modern) Installer Control recheck, if successful it's going to update
 * If not trying to run inspection host without installation
 */
function installerControlRecheck() {
    divLog('installerControlRecheck - enter');

    if (countdown > 0 && !checkActiveX(COMPONENT_ID.INSTALLER_CONTROL_MODERN)) {
        divLog(`installerControlRecheck ${countdown} - not installed yet`);

        timerId = window.setTimeout(() => installerControlRecheck(), 2000);
        countdown--;
    } else {
        divLog('installerControlRecheck - installed, rechecking');

        window.clearTimeout(timerId);
        container.innerHTML = ''; // removing object tage
        if (checkActiveX(COMPONENT_ID.INSTALLER_CONTROL_MODERN)) {
            setImmediate(() => installerControlUpdate());
        } else {
            // OnControlEvent("<?echo($a_params['name'])?>", "error", -1);
            callback(EVENT.INSTALLER_CONTROL_RECHECK, String(STATUS.CREATE_FAILED), '');
            setImmediate(() => inspectionHostRun(false));
        }
    }

    divLog('installerControlRecheck - exit');
}

/**
 * Checks if (Modern) Installer Control is installed and trying to update it
 * Callback function is used
 */
function installerControlUpdate() {
    divLog('installerControlUpdate - enter');

    try {
        const attributes = {
            CLASSID: packages.installerControl.id,
        };
        const url = baseUrl + packages.inspectionHost.url + '#' + packages.inspectionHost.version;
        const params = {
            AutoLoad: '0',
            InstallerServiceUpdate:
                `/URL:${baseUrl}${packages.installerService2015.url}` +
                ` /ID: ${packages.installerService2015.id}` +
                `\n/URL:${baseUrl}${packages.installerService.url}` +
                ` /ID: ${packages.installerService.id}`,
            InstallSequence:
                `/URL:${baseUrl}${packages.installerControl.url}` +
                ` /ID: ${packages.installerControl.id}` +
                ` /TASKID: ${baseUrl}${packages.installerControl.url}` +
                `\n/URL:${url}` +
                ` /ID: ${packages.inspectionHost.id}` +
                ` /TASKID: ${url}` +
                `\n`,
        };
        const events = {
            'InstallProgress(a_taskID, a_state, a_progress)':
                EVENT.INSTALLER_CONTROL_UPDATE + '(a_taskID, a_state, a_progress);',
        };
        createObject(objectId, attributes, params, events);
        // find and run object
        const object = document.getElementById(objectId);
        (object as any).run();
    } catch (e) {
        divLog('updateInstallerControl Exception: ' + e.description);
        callback(EVENT.INSTALLER_CONTROL_UPDATE, String(STATUS.CREATE_FAILED), '');
    }

    divLog('installerControlUpdate - exit');
}

/**
 * installerControlUpdate's object callback
 * @param taskId execution tracking number
 * @param status installation status
 * @param prog progress string
 */
window[EVENT.INSTALLER_CONTROL_UPDATE] = (taskId: number, status: STATUS, prog: number) => {
    divLog(`F:${EVENT.INSTALLER_CONTROL_UPDATE} - enter tid=${taskId}, ${status}, ${prog}`);

    callback(EVENT.INSTALLER_CONTROL_UPDATE, String(status), String(prog));
    if (String(status) === String(STATUS.DONE)) {
        setImmediate(() => inspectionHostRun(true));
    }

    divLog(`${EVENT.INSTALLER_CONTROL_UPDATE} - exit`);
};

/**
 * Running inspection host
 * @param fromRecheck indicates wheter call is coming from callback or recheck fallback
 */
function inspectionHostRun(fromRecheck: boolean) {
    divLog('inspectionHostRun - enter ' + String(fromRecheck));

    if (!checkActiveX(COMPONENT_ID.INSPECTION_HOST)) {
        callback(EVENT.INSPECTION_HOST_INSTALL, String(STATUS.CREATE_FAILED), '');
        return; // ATTENTION RETURN
    }

    callback(EVENT.INSPECTION_HOST_INSTALL, String(STATUS.STARTED), '');
    const controlId = 'InspectionHostCTRL';
    const attributes = {
        CLASSID: packages.inspectionHost.id,
        CODEBASE: baseUrl + packages.inspectionHost.url,
        onerror: `${EVENT.INSPECTION_HOST_ERROR}();`,
        border: '0',
    };
    const params = {
        controlName: 'InspectionHost.UAgentHost',
        AgentUrl: baseUrl + `/my.policy_host?sess=${session}&dummy=0`,
        HostRootUrl: baseUrl,
        ClientData: '',
        AutoLoad: 1,
        ur_noUI: 1,
        ValidateAgentUrl: 1,
        Session: session,
        pluginHostVersion: packages.pluginHost.version,
    };

    const ret = `return ${EVENT.INSPECTION_HOST_EVENT}`;
    const events = {
        'OnCallScript(a_name, a_params)': `${ret}("OnCallScript", a_name, a_params)`,
        'OnCallScriptWithReturnValue(a_name, a_params)': `${ret}("OnCallScriptWithReturnValue", a_name, a_params)`,
    };
    createObject(controlId, attributes, params, events);

    divLog('inspectionHostRun - exit');
}

/**
 * Inspection host error callback
 * Unknown params
 */
window[EVENT.INSPECTION_HOST_ERROR] = (...args: string[]) => {
    divLog(`F:${EVENT.INSPECTION_HOST_ERROR} - enter ` + args.join(', '));

    callback(EVENT.INSPECTION_HOST_ERROR, args.join(', '), '');

    divLog(`${EVENT.INSPECTION_HOST_ERROR} - exit`);
};

/** events of inspection host */
enum HOST_EVENT {
    EXIT = 'fExit',
    GOTO_URL = 'fGoToURL',
    DEAD_END = 'fDeadEnd',
}

/** protos of HOST_EVENT.EXIT */
enum EXIT_PROTO {
    DEAD_END = 'deadend://',
    JS = 'javascript:',
}

/** supported functions of HOST_EVENT.EXIT EXIT_PROTO.JS */
enum JS_FNAME {
    SET_TEXT = 'settext',
    SET_PROGRESS = 'install_control_event',
}

/**
 * Inspection host event receiver
 * @param caller OnCallScript, OnCallScriptWithReturnValue or itself
 * @param event name of event
 * @param arg1 argument 1 of event
 * @param arg2 argument 2 of event
 */
window[EVENT.INSPECTION_HOST_EVENT] = (caller: string, event: HOST_EVENT, arg1: string, arg2: string) => {
    divLog(`${EVENT.INSPECTION_HOST_EVENT} - enter ${caller}, ${event}, ${arg1}, ${arg2}`);

    switch (event) {
        case HOST_EVENT.EXIT:
            [, , arg1] = arg1.match(/^(['|"])([^\1]+)\1/) || ['', '', arg1];
            // in case of deadend://
            if (arg1.indexOf(EXIT_PROTO.DEAD_END) === 0) {
                // callback should handle this situation
                callback(EVENT.INSPECTION_HOST_EVENT, HOST_EVENT.DEAD_END, arg1.substr(EXIT_PROTO.DEAD_END.length));
            } else if (arg1.indexOf(EXIT_PROTO.JS) === 0) {
                // in case of javascript:fname("a", "b"...
                arg1 = arg1.substr(EXIT_PROTO.JS.length);
                // split function names and argument string
                const [, fname, fargstr] = arg1.match(/([^(]+)\('([^)]+)'\)/) || ['', '', ''];
                // split arguments
                const fargs = fargstr.split(/'\s?,\s?'/);
                switch (fname) {
                    case JS_FNAME.SET_TEXT:
                        callback(EVENT.CHANGE_TEXT, fargs[0], '');
                        break;
                    case JS_FNAME.SET_PROGRESS:
                        callback(EVENT.CHANGE_PROGRESS, fargs[0], fargs[1]);
                        break;
                    default:
                        alert(`proto_js  ${fname} ${fargs} is not supported`);
                }
            } else {
                window[EVENT.INSPECTION_HOST_EVENT](EVENT.INSPECTION_HOST_EVENT, HOST_EVENT.GOTO_URL, arg1, arg2);
            }
            break;
        case HOST_EVENT.GOTO_URL: {
            // get rid of quotes
            arg1 = arg1.substr(1, arg1.length - 2);
            if (arg1.indexOf(baseUrl) === 0) {
                arg1 = arg1.substr(baseUrl.length);
            }
            /* eslint-disable-next-line prefer-const */
            let [url, farg] = arg1.split(/",\s?"/);
            if (url.indexOf('/redirect:') === 0) {
                url = unescape(url.substring(10, url.length));
            } else if (url.indexOf('/logon') === 0) {
                url = '/my.policy';
            } else if (url.indexOf('/logout') === 0) {
                url = '/my.logout.php3';
            } else if (url.indexOf('/policy/show_error.php3') >= 0) {
                // Client doesn't supprot this check, let's skip
                if (url.indexOf('error_number=10014&') >= 0 || url.indexOf('error_number=10001&') >= 0) {
                    callback(EVENT.NO_INSPECTION_HOST, unescape(url), farg);
                    break;
                }
                url = url
                    .replace('/policy/show_error.php3', '/my.logout.php3')
                    .replace(/error_number=\d*/, 'errorcode=1');
            }
            callback(EVENT.CHANGE_URL, unescape(url), farg);
            break;
        }
        default:
            alert(`unsupported call to ${EVENT.INSPECTION_HOST_EVENT}` + `(${caller}, ${event}, ${arg1}, ${arg2})`);
    }

    divLog(`${EVENT.INSPECTION_HOST_ERROR} - exit`);
};

export { installActiveX, EVENT, STATUS };
